import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, History, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';
import { formatDate } from '@/lib/utils';

const ENTITY_TYPES = [
  ['', 'Tümü / All'],
  ['TradingInvoice', 'Fatura / Invoice'],
  ['TradingPayment', 'Tahsilat-Ödeme / Payment'],
  ['TradingCreditNote', 'İade faturası / Credit note'],
  ['TradingSupplierInvoice', 'Tedarikçi faturası / Supplier invoice'],
  ['TradingDelivery', 'Sevkiyat / Delivery'],
  ['TradingGoodsReceipt', 'Mal kabul / Goods receipt'],
  ['TradingJournalEntry', 'Muhasebe fişi / Journal entry'],
  ['CariTieOut', 'Cari mutabakat / Tie-out'],
  ['PeriodEnd', 'Dönem sonu / Period end'],
] as const;

const ACTION_LABELS: Record<string, string> = {
  'trading_invoice.created': 'Fatura oluşturuldu',
  'trading_invoice.sent': 'Fatura gönderildi',
  'trading_invoice.paid': 'Fatura ödendi olarak işaretlendi',
  'trading_invoice.cancelled': 'Fatura iptal edildi',
  'trading_invoice.written_off': 'Fatura silindi (zarar yazıldı)',
  'trading_payment.recorded': 'Tahsilat/ödeme kaydedildi',
  'trading_payment.allocated': 'Ödeme faturalara eşleştirildi',
  'trading_payment.voided': 'Ödeme iptal edildi',
  'trading_credit_note.issued': 'İade faturası kesildi',
  'trading_credit_note.applied': 'İade faturası uygulandı',
  'trading_credit_note.application_reversed': 'İade uygulaması geri alındı',
  'trading_credit_note.cancelled': 'İade faturası iptal edildi',
  'trading_supplier_invoice.created': 'Tedarikçi faturası girildi',
  'trading_supplier_invoice.approved': 'Tedarikçi faturası onaylandı',
  'trading_supplier_invoice.cancelled': 'Tedarikçi faturası iptal edildi',
  'trading_delivery.delivered': 'Sevkiyat teslim edildi',
  'trading_goods_receipt.created': 'Mal kabul yapıldı',
  'trading_journal.posted': 'Fiş işlendi',
  'trading_journal.voided': 'Fiş iptal edildi',
  'accounting.cari_remirror': 'Cari defteri yeniden aktarıldı',
  'accounting.fx_revaluation.post': 'Kur değerlemesi oluşturuldu',
  'accounting.fx_revaluation.void': 'Kur değerlemesi iptal edildi',
  'accounting.year_end_close.post': 'Yıl sonu kapanış fişleri oluşturuldu',
  'accounting.year_end_close.void': 'Yıl sonu kapanış iptal edildi',
};

const ENTITY_HREF: Record<string, (id: string) => string> = {
  TradingInvoice: (id) => `/trading/finance/invoices/${id}`,
  TradingPayment: (id) => `/trading/finance/payments/${id}`,
  TradingSupplierInvoice: (id) => `/trading/finance/supplier-invoices/${id}`,
  TradingJournalEntry: (id) => `/trading/accounting/journal-entries/${id}`,
};

const SUMMARY_KEYS = ['paymentNumber', 'invoiceNumber', 'entryNumber', 'amount', 'currency', 'reason', 'entries', 'rows', 'voided', 'from', 'to'];

function summarize(details: Record<string, unknown> | null): string {
  if (!details) return '';
  const flat: Record<string, unknown> = { ...details, ...((details.input as Record<string, unknown>) ?? {}), ...((details.output as Record<string, unknown>) ?? {}) };
  return SUMMARY_KEYS.filter((k) => flat[k] != null && typeof flat[k] !== 'object')
    .map((k) => `${k}: ${String(flat[k])}`)
    .join(' · ');
}

/** Hareket günlüğü — the trading audit trail (who / when / what) written by the audited() middleware and the period-end tools. */
export function TradingActivityFeedPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [entityType, setEntityType] = useState('');
  const [userId, setUserId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const options = trpc.activityFeed.filterOptions.useQuery({ app: 'TRADING' });
  const { data, isLoading, error } = trpc.activityFeed.list.useQuery({
    app: 'TRADING',
    page,
    limit: 40,
    search: search || undefined,
    entityType: entityType || undefined,
    userId: userId || undefined,
    startDate: startDate || undefined,
    endDate: endDate ? `${endDate}T23:59:59` : undefined,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <History className="h-6 w-6" />
          Hareket Günlüğü / Audit Trail
        </h1>
        <p className="text-muted-foreground">Ticaret tarafındaki her mali işlemin kim / ne zaman / ne kaydı.</p>
      </div>

      <div className="bg-card flex flex-wrap items-end gap-3 rounded-lg border p-4">
        <div className="space-y-1">
          <Label>Ara / Search</Label>
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="PAY-2026-…, INV-…" className="h-9 w-56" />
        </div>
        <div className="space-y-1">
          <Label>Belge türü</Label>
          <select value={entityType} onChange={(e) => { setEntityType(e.target.value); setPage(1); }} className="flex h-9 w-56 rounded-md border border-input bg-background px-3 text-sm">
            {ENTITY_TYPES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label>Kullanıcı</Label>
          <select value={userId} onChange={(e) => { setUserId(e.target.value); setPage(1); }} className="flex h-9 w-48 rounded-md border border-input bg-background px-3 text-sm">
            <option value="">Tümü</option>
            {(options.data?.users ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.firstName} {u.lastName}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label>Başlangıç</Label>
          <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} className="h-9 w-40" />
        </div>
        <div className="space-y-1">
          <Label>Bitiş</Label>
          <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} className="h-9 w-40" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : error ? (
        <div className="border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">{error.message}</div>
      ) : data ? (
        <>
          <div className="bg-card overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Zaman</th>
                  <th className="px-4 py-2 font-medium">Kullanıcı</th>
                  <th className="px-4 py-2 font-medium">İşlem</th>
                  <th className="px-4 py-2 font-medium">Belge</th>
                  <th className="px-4 py-2 font-medium">Detay</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-4 text-sm text-muted-foreground">
                      Kayıt yok. / No activity.
                    </td>
                  </tr>
                )}
                {data.items.map((item) => {
                  const href = ENTITY_HREF[item.entityType]?.(item.entityId);
                  return (
                    <tr key={item.id} className="border-b last:border-b-0">
                      <td className="px-4 py-2 whitespace-nowrap text-xs">{formatDate(item.createdAt, { dateStyle: 'short', timeStyle: 'short' })}</td>
                      <td className="px-4 py-2">{item.user?.name ?? <span className="text-muted-foreground">sistem</span>}</td>
                      <td className="px-4 py-2">
                        <div>{ACTION_LABELS[item.action] ?? item.action}</div>
                        <div className="font-mono text-[11px] text-muted-foreground">{item.action}</div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="text-xs text-muted-foreground">{item.entityType}</div>
                        {href ? (
                          <Link to={href} className="font-mono text-xs hover:underline">
                            {item.entityId}
                          </Link>
                        ) : (
                          <span className="font-mono text-xs">{item.entityId}</span>
                        )}
                      </td>
                      <td className="max-w-[420px] px-4 py-2 text-xs text-muted-foreground">{summarize(item.details)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {data.pagination.total} kayıt · sayfa {data.pagination.page}/{Math.max(1, data.pagination.totalPages)}
            </span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" className="h-8" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" className="h-8" disabled={page >= data.pagination.totalPages} onClick={() => setPage((p) => p + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
